import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { SignaturePad } from "@/components/SignaturePad";
import { Loader2, CheckCircle2, Download, ShieldCheck } from "lucide-react";

export default function PublicContract() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sig, setSig] = useState({ name: "", mode: "type", image: "" });
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const load = () => {
    api
      .get(`/public/contracts/${token}`)
      .then((r) => {
        setData(r.data);
        setDone(r.data.client_signed);
      })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
  }, [token]);

  const sign = async () => {
    if (!sig.name.trim()) return toast.error("Type your full name to sign");
    if (!agreed) return toast.error("Please confirm you agree to the terms");
    setSubmitting(true);
    try {
      await api.post(`/public/contracts/${token}/sign`, sig);
      setDone(true);
      toast.success("Signed! Thank you.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not sign");
    } finally {
      setSubmitting(false);
    }
  };

  const downloadPdf = () => {
    window.open(`${API}/public/contracts/${token}/pdf`, "_blank");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background text-center px-6">
        <div>
          <h1 className="font-display text-2xl font-semibold">Contract not found</h1>
          <p className="text-muted-foreground mt-2">This signing link is invalid or has expired.</p>
        </div>
      </div>
    );
  }

  const company = data.company || {};

  return (
    <div className="min-h-screen bg-muted/40 py-10 px-4">
      <div className="max-w-3xl mx-auto">
        <Card className="p-8 md:p-10">
          {/* Brand header */}
          <div className="flex items-center gap-3 pb-5 border-b border-border">
            {company.logo_url ? (
              <img src={`${API.replace(/\/api$/, "")}${company.logo_url}`} alt="" className="h-12 w-12 object-contain" />
            ) : null}
            <div>
              <p className="font-display text-xl font-bold">{company.name}</p>
              {(company.address || company.phone) && (
                <p className="text-xs text-muted-foreground">
                  {[company.address, company.phone].filter(Boolean).join(" · ")}
                </p>
              )}
            </div>
          </div>

          <h1 className="font-display text-2xl font-semibold mt-6" data-testid="public-contract-title">
            {data.title}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Prepared for {data.client_name}
            {data.property_name ? ` · ${data.property_name}` : ""}
          </p>

          <div className="mt-6 whitespace-pre-wrap text-sm leading-relaxed text-foreground/90" data-testid="public-contract-body">
            {data.body}
          </div>

          <div className="mt-6 flex items-center gap-2 text-sm">
            {data.owner_signed ? (
              <Badge className="bg-emerald-100 text-emerald-700 border-0 gap-1">
                <CheckCircle2 className="h-3.5 w-3.5" /> Signed by {data.owner_signer || company.name}
              </Badge>
            ) : (
              <Badge variant="outline">Awaiting company signature</Badge>
            )}
          </div>

          <div className="mt-8 pt-6 border-t border-border">
            {done ? (
              <div className="text-center py-6" data-testid="public-signed-confirmation">
                <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto mb-3" />
                <h2 className="font-display text-xl font-semibold">You've signed this contract</h2>
                <p className="text-muted-foreground text-sm mt-1">
                  A copy has been recorded. You can download the signed PDF below.
                </p>
                <Button variant="outline" className="mt-4" onClick={downloadPdf} data-testid="public-download-pdf">
                  <Download className="h-4 w-4 mr-2" /> Download signed contract
                </Button>
              </div>
            ) : (
              <>
                <h2 className="font-display text-lg font-semibold mb-1">Your signature</h2>
                <p className="text-sm text-muted-foreground mb-4">
                  Sign below to accept the terms of this agreement.
                </p>
                <SignaturePad onChange={setSig} testidPrefix="client-sig" />
                <label className="flex items-start gap-2 mt-4 cursor-pointer text-sm">
                  <input
                    type="checkbox"
                    checked={agreed}
                    onChange={(e) => setAgreed(e.target.checked)}
                    className="mt-1"
                    data-testid="public-agree-checkbox"
                  />
                  <span>
                    I have read and agree to the terms of this agreement, and consent to signing
                    electronically.
                  </span>
                </label>
                <Button
                  className="w-full mt-5 h-11"
                  onClick={sign}
                  disabled={submitting}
                  data-testid="public-sign-button"
                >
                  {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  Sign &amp; accept agreement
                </Button>
                <p className="text-[11px] text-muted-foreground mt-3 flex items-center justify-center gap-1">
                  <ShieldCheck className="h-3 w-3" /> Secure e-signature · your IP &amp; timestamp are recorded
                </p>
              </>
            )}
          </div>
        </Card>
        <p className="text-center text-xs text-muted-foreground mt-4">
          Powered by SmartHost Solutions
        </p>
      </div>
    </div>
  );
}
